<script setup lang="ts">
import { computed } from 'vue'
import type { Substandard } from '@/standards/standard_types'
import { LIST_STYLE_CLASS_BY_MARKER_TYPE, type ListMarkerType } from '@/standards/list_markers'

const props = withDefaults(
  defineProps<{
    orderedSubstandards?: Substandard[] | null
    olType?: ListMarkerType
    singleSubstandardBullet?: string
    singleSubstandardText?: string
  }>(),
  {
    orderedSubstandards: null,
    olType: 'a',
    singleSubstandardBullet: '',
    singleSubstandardText: ''
  }
)

const listClass = computed(() => ['mb-2.5 pl-10', LIST_STYLE_CLASS_BY_MARKER_TYPE[props.olType]])

const isFullListShown = computed(() => !props.singleSubstandardBullet && Boolean(props.orderedSubstandards))
const isSingleSubstandardShown = computed(() => Boolean(props.singleSubstandardBullet && props.singleSubstandardText))

const ROMAN_NUMERAL_VALUES: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100 }

function isNumeral(bullet: string): boolean {
  return !isNaN(parseInt(bullet))
}

function romanNumeralValue(numeral: string): number {
  const values = [...numeral.toLowerCase()].map((character) => ROMAN_NUMERAL_VALUES[character] ?? 0)
  return values.reduce((total, value, index) => {
    const isSubtractive = value < (values[index + 1] ?? 0)
    return isSubtractive ? total - value : total + value
  }, 0)
}

const singleSubstandardStart = computed(() => {
  const bullet = props.singleSubstandardBullet
  if (isNumeral(bullet)) {
    return bullet
  }
  if (props.olType === 'i') {
    return romanNumeralValue(bullet)
  }
  return bullet.charCodeAt(0) - props.olType.charCodeAt(0) + 1
})

function substandardHtml(substandard: Substandard): string {
  return substandard.substandard_text || substandard.standard_text
}
</script>

<template>
  <div data-testid="substandard-bullets">
    <ol v-if="isFullListShown" :type="olType" :class="listClass">
      <li
        v-for="substandard in orderedSubstandards"
        :key="substandard.standard_long_ref"
        v-html="substandardHtml(substandard)"
      />
    </ol>
    <ol v-if="isSingleSubstandardShown" :type="olType" :start="singleSubstandardStart" :class="listClass">
      <li v-html="singleSubstandardText" />
    </ol>
  </div>
</template>
